import { useState, useEffect } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, Shield, UserCheck, UserCog, Plus, Trash2, Loader2, UserPlus, Eye, EyeOff, KeyRound } from "lucide-react";

const ROLES = [
  { value: "super_admin", label: "Super Admin", color: "bg-purple-100 text-purple-700" },
  { value: "admin", label: "Admin", color: "bg-blue-100 text-blue-700" },
  { value: "assessor", label: "Assessor", color: "bg-amber-100 text-amber-700" },
  { value: "iqa", label: "IQA", color: "bg-green-100 text-green-700" },
  { value: "industry_specialist", label: "Industry Specialist", color: "bg-indigo-100 text-indigo-700" },
  { value: "candidate", label: "Candidate", color: "bg-teal-100 text-teal-700" },
];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "admin", temp_password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rolesFromApi, setRolesFromApi] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [roleDisplayName, setRoleDisplayName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [savingRole, setSavingRole] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get("/users/roles");
      setRolesFromApi(response.data.roles || []);
    } catch (error) {
      console.error("Failed to load roles");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.temp_password) {
      toast.error("Name, email and temporary password are required");
      return;
    }
    
    if (newUser.temp_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/users", newUser);
      toast.success("User created! Welcome email with credentials sent.");
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", role: "admin", temp_password: "" });
      setShowPassword(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRole = async () => {
    if (!editingRole || !roleDisplayName.trim()) return;
    setSavingRole(true);
    try {
      await api.put(`/roles/${editingRole}`, {
        display_name: roleDisplayName,
        description: roleDescription,
      });
      toast.success("Role updated");
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setSavingRole(false);
    }
  };

  const getRoleDisplayName = (roleKey) => {
    const fromApi = rolesFromApi.find(r => r.role_key === roleKey);
    if (fromApi) return fromApi.display_name;
    const fromLocal = ROLES.find(r => r.value === roleKey);
    return fromLocal ? fromLocal.label : roleKey;
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setSubmitting(true);
    try {
      await api.delete(`/users/${userToDelete.user_id}`);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.name} (${user.email}) to "User@123"?\n\nThey will receive an email with the new password and be forced to change it on next login.`)) return;
    try {
      const res = await api.put(`/users/${user.user_id}/reset-password`, { password: "User@123" });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[0];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500 mt-1">Manage platform users and their roles</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new staff member. A welcome email with login credentials will be sent automatically.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="new-user-name"
                    placeholder="John Doe"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="new-user-email"
                    type="email"
                    placeholder="john@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {rolesFromApi.filter(r => r.role_key !== "candidate" && r.role_key !== "super_admin").map((role) => (
                        <SelectItem key={role.role_key} value={role.role_key}>
                          {role.display_name}
                          {role.can_login === false && " (email link only)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newUser.role === "industry_specialist" && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">This role does not have system login access. They score interviews via email links only.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temp_password">Temporary Password *</Label>
                  <div className="relative">
                    <Input
                      id="temp_password"
                      data-testid="new-user-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={newUser.temp_password}
                      onChange={(e) => setNewUser({ ...newUser, temp_password: e.target.value })}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">User must change this password on first login</p>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateUser} disabled={submitting} data-testid="confirm-create-user">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create User
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700"
                disabled={submitting}
              >
                {submitting ? "Deleting..." : "Delete User"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Role Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { role: "admin", icon: Shield, color: "bg-blue-100", iconColor: "text-blue-600" },
            { role: "assessor", icon: UserCheck, color: "bg-amber-100", iconColor: "text-amber-600" },
            { role: "iqa", icon: UserCog, color: "bg-green-100", iconColor: "text-green-600" },
            { role: "industry_specialist", icon: Users, color: "bg-indigo-100", iconColor: "text-indigo-600" },
            { role: "candidate", icon: UserPlus, color: "bg-teal-100", iconColor: "text-teal-600" },
          ].map((item) => (
            <Card key={item.role} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center`}>
                    <item.icon className={`w-5 h-5 ${item.iconColor}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {users.filter(u => u.role === item.role).length}
                    </p>
                    <p className="text-xs text-slate-500">{getRoleDisplayName(item.role)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Users Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : users.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    return (
                      <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={user.picture} />
                              <AvatarFallback className="bg-slate-200 text-slate-600 text-sm">
                                {user.name?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-slate-900">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={roleInfo.color}>{getRoleDisplayName(user.role)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            {user.status || "active"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={user.role}
                              onValueChange={(value) => handleRoleChange(user.user_id, value)}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`role-select-${user.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROLES.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleResetPassword(user)}
                              data-testid={`reset-pw-${user.user_id}`}
                              title="Reset password to User@123"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Users Yet</h3>
                <p className="text-slate-500">Users will appear here after they sign in</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Management */}
        <Card className="border-slate-200" data-testid="role-management-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Role Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">Rename roles to match your organization's terminology. Permissions are predefined per template.</p>
            <div className="space-y-3">
              {rolesFromApi.filter(r => r.role_key !== "candidate").map((role) => (
                <div key={role.role_key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{role.display_name}</p>
                      <Badge variant="outline" className="text-xs">{role.role_key}</Badge>
                      {role.can_login === false && <Badge className="bg-slate-200 text-slate-600 text-[10px]">No Login</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
                    {role.permissions && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {role.permissions.slice(0, 4).map(p => (
                          <span key={p} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{p.replace(/_/g, " ")}</span>
                        ))}
                        {role.permissions.length > 4 && (
                          <span className="text-[10px] text-slate-400">+{role.permissions.length - 4} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingRole(role.role_key);
                      setRoleDisplayName(role.display_name);
                      setRoleDescription(role.description || "");
                    }}
                    data-testid={`edit-role-${role.role_key}`}
                  >
                    Rename
                  </Button>
                </div>
              ))}
            </div>

            {/* Inline Edit */}
            {editingRole && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <h4 className="font-medium text-slate-900">Rename Role: {editingRole}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Display Name</Label>
                    <Input
                      value={roleDisplayName}
                      onChange={(e) => setRoleDisplayName(e.target.value)}
                      placeholder="e.g., Internal Assessor"
                      data-testid="role-display-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={roleDescription}
                      onChange={(e) => setRoleDescription(e.target.value)}
                      placeholder="Short description"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveRole} disabled={savingRole} data-testid="save-role-btn">
                    {savingRole ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingRole(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
